import { Button, Form, Input } from 'antd';
import { Rule } from 'antd/es/form';
import { useCallback, useEffect, useRef, useState } from 'react';

import { Text, useTheme } from '@desygner/ui-common-components';

import { CONSTANTS } from '../../AuthModal.data';
import {
  StyledExtraActionsWrapper,
  StyledModalSubtitle,
  StyledOrDivider,
  StyledSpacer,
  getPrimaryButtonStyle,
  styleLinkButton,
} from '../../AuthModal.styles';
import { AuthViewPropsT, LoginPasswordViewLabelsT } from '../../AuthModal.types';
import { focusInput, getServerErrorMessage, handleEnter } from '../../AuthModal.utils';
import { SocialProvidersView } from '../../components/SocialProviders/SocialProviders';
import { StepHeader } from '../../components/StepHeader/StepHeader';
import { useAuthContext } from '../../context/AuthContext';

export const LoginPasswordView = ({ ...props }: AuthViewPropsT) => {
  const [isEmailReset, setIsEmailReset] = useState(false);

  const { ...labels } = props.labels as LoginPasswordViewLabelsT;
  const {
    isLoading,
    setIsLoading,
    isLoadingResend,
    setIsLoadingResend,
    setServerError,
    invitationToken,
  } = useAuthContext();
  const { apiLogin, apiSendResetToken } = useAuthContext();

  const form = Form.useFormInstance();
  const email = form.getFieldValue(CONSTANTS.form.emailFieldName);

  const passwordInputRef = useRef<any>(null);
  const theme = useTheme();

  useEffect(() => {
    focusInput(passwordInputRef);
  }, []);

  const handleLogin = useCallback(async () => {
    if (isLoading) return;

    setIsLoading?.(true);
    await form
      .validateFields()
      .then(async () => {
        setIsLoading?.(true);
        setServerError?.(null);

        const password = form.getFieldValue(CONSTANTS.form.passwordFieldName);

        await apiLogin?.({
          email,
          password,
          invitation_token: invitationToken,
          ...(props.isDesygnerUser ? { desygner_email: email } : {}),
        })
          .then((res) => {
            if (!res.data.token) {
              console.error('User token is', res?.data?.token);
              setServerError?.(labels.errors.e500);
              return;
            }

            props.onLogin?.(res.data.token, 'loginPassword');
            props.onClose?.();
          })
          .catch((err) => {
            const { status } = err.response;

            if (status === 401) {
              form.setFields([
                {
                  name: CONSTANTS.form.passwordFieldName,
                  errors: [labels.errors.e401],
                },
              ]);
            } else if (status >= 500) {
              setServerError?.(labels.errors.e500);
            }
          })
          .finally(() => {
            setIsLoading?.(false);
          });
      })
      .catch((err) => {
        console.error(err);
        setServerError?.(labels.errors.e500);
      })
      .finally(() => {
        setIsLoading?.(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiLogin, form, props, setIsLoading, setServerError]);

  const rules: Rule[] = [
    {
      required: true,
      message: labels.errors.client.required,
    },
  ];

  const handleResetEmail = useCallback(async () => {
    if (isLoadingResend) return;

    if (!email) {
      setServerError?.(labels.errors.e500);
      return;
    }

    setIsLoadingResend?.(true);
    setServerError?.(null);

    await apiSendResetToken?.(email)
      .then(() => {
        setIsEmailReset(true);
      })
      .catch((err) => {
        const serverErrorMessage = getServerErrorMessage(err) ?? labels.errors.e500;

        setServerError?.(serverErrorMessage);
      })
      .finally(() => {
        setIsLoadingResend?.(false);
      });
  }, [
    apiSendResetToken,
    email,
    isLoadingResend,
    labels.errors.e500,
    setIsLoadingResend,
    setServerError,
  ]);

  return (
    <>
      <StepHeader title={labels.title} onBack={() => props.onChangeView?.('loginEmail')} />

      <StyledModalSubtitle>
        <Text size="Body">{labels.subtitle}</Text>
      </StyledModalSubtitle>

      {/* HIDDEN EMAIL FIELD FOR BROWSER AUTO FILL - DO NOT REMOVE */}
      <Form.Item name={CONSTANTS.form.emailFieldName} label={'Email'} hidden>
        <Input value={email} />
      </Form.Item>

      <Form.Item name={CONSTANTS.form.passwordFieldName} rules={[...rules]}>
        <Input.Password
          placeholder={labels.form.placeholder}
          autoComplete="current-password"
          ref={passwordInputRef}
          onKeyDown={(event) => handleEnter(event, handleLogin)}
          onChange={(event) => {
            form.setFieldValue(CONSTANTS.form.passwordFieldName, event.target.value);
          }}
        />
      </Form.Item>
      <Button
        type="primary"
        loading={isLoading}
        onClick={handleLogin}
        data-action="auth-email-password-login"
        style={getPrimaryButtonStyle(theme)}
      >
        {labels.cta.continue}
      </Button>

      <StyledOrDivider>
        <Text size="Body">{labels.or}</Text>
      </StyledOrDivider>

      <SocialProvidersView
        isCompact
        anotherWayLabel={labels.cta.anotherWay}
        continueWithLabel={labels.cta.continueWith}
      />

      <StyledExtraActionsWrapper>
        <Text size="Body">{labels.forgotPassword}</Text>
        <Button
          data-action="auth-forgot-password"
          type="link"
          style={styleLinkButton}
          loading={isLoadingResend}
          onClick={() => handleResetEmail()}
        >
          {labels.cta.resetPassword}
        </Button>
      </StyledExtraActionsWrapper>
      {isEmailReset && (
        <StyledSpacer>
          <Text size="Small" color={theme.Color.Neutrals.Gray}>
            {labels.getResetPasswordSuccessMessage(email)}
          </Text>
        </StyledSpacer>
      )}
    </>
  );
};
